import Automerge from "automerge";
import { Change, diffChars } from "diff";
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import { SolvyDocument } from "../types";
import { newSerialQueue, SerialQueue } from "./serialQueue";
import { debugLog } from "./util";

const chunkString = (str: string, size: number) => {
  var i,
    j,
    chunk = size,
    out: string[] = [];
  for (i = 0, j = str.length; i < j; i += chunk) {
    out.push(str.slice(i, i + chunk));
  }
  return out;
};

const diffCharsAsync = async (
  oldStr: string,
  newStr: string
): Promise<Change[]> => {
  return new Promise<Change[]>((resolve, reject) => {
    diffChars(oldStr, newStr, (err, changes) => {
      if (err) {
        reject(err);
        return;
      }
      if (!changes) {
        resolve([{ count: oldStr.length, value: oldStr }]);
      } else {
        resolve(changes);
      }
    });
  });
};

type ProgramSyncState = {
  program: string;
  busy: boolean;
};

type BidirectionalState<S> = {
  previous: S;
  current: S;
  rx: boolean;
};

type BidirectionalSetState<S> = {
  rx: React.Dispatch<S>;
  tx: React.Dispatch<S>;
};

function useBidirectionalState<S>(
  initialState: () => S
): [BidirectionalState<S>, BidirectionalSetState<S>] {
  const [v, setv] = useState<BidirectionalState<S>>(() => {
    const iv = initialState();
    return {
      previous: iv,
      current: iv,
      rx: false,
    };
  });

  return [
    v,
    {
      rx: (value: S): void => {
        setv((current) => {
          return {
            previous: current.current,
            current: value,
            rx: true,
          };
        });
      },
      tx: (value: S): void => {
        setv((current) => {
          return {
            previous: current.current,
            current: value,
            rx: false,
          };
        });
      },
    },
  ];
}

function analyzeDiff(
  value: BidirectionalState<string>,
  sq: SerialQueue<any>,
  amDoc: MutableRefObject<Automerge.FreezeObject<SolvyDocument>>
) {
  if (value.previous === value.current || value.rx) {
    debugLog("ignoring changes", { ...value });
    return;
  }
  // kick off the diffing process _synchronously_ so that the value
  // of `program` doesn't change underneath us
  const diffPromise = diffCharsAsync(value.previous, value.current);

  // push the diff to changes process into a serial queue that will ensure
  // each version of that will update the AM doc serially
  sq.push(async (): Promise<any> => {
    const changes = await diffPromise;
    debugLog("processing changes...", { changes });
    amDoc.current = Automerge.change<SolvyDocument>(amDoc.current, (doc_1) => {
      let offset: number = 0;
      const text = doc_1.program;
      for (let change of changes) {
        if (change.added) {
          text.insertAt && text.insertAt(offset, ...change.value.split(""));
          offset += change.count || 0;
        } else if (change.removed) {
          text.deleteAt && text.deleteAt(offset, change.value.length);
        } else {
          offset += change.count || 0;
        }
      }
    });
  });
}

function toHexString(bytes: Uint8Array): string {
  return bytes.reduce(
    (str: string, byte: number) => str + byte.toString(16).padStart(2, "0"),
    ""
  );
}

function fromHexString(hexString: string): Automerge.BinaryDocument {
  const ua = new Uint8Array(
    chunkString(hexString, 2).map((byte) => parseInt(byte, 16))
  );
  (ua as Automerge.BinaryDocument).__binaryDocument = true;
  return ua as Automerge.BinaryDocument;
}

// Build the document the hook starts from, restoring the previous session out
// of localStorage when there is one. This runs during state initialization
// rather than in an effect so that a remount (which StrictMode forces in
// development) cannot observe a half-initialized document and clobber it.
function loadInitialDocument(): Automerge.FreezeObject<SolvyDocument> {
  const savedDocData = localStorage.getItem("solvy.document");
  if (!savedDocData) {
    return Automerge.from<SolvyDocument>({
      name: new Automerge.Text(""),
      program: new Automerge.Text(""),
    });
  }
  debugLog("localstorage_program_restore");
  // The saved document is adopted wholesale rather than merged into a fresh
  // one. Both would have been seeded by their own `Automerge.from`, making
  // `program` a concurrent assignment on either side; merging then picks a
  // winner by actor id and drops the saved text roughly half the time.
  return Automerge.load<SolvyDocument>(fromHexString(savedDocData));
}

export function useProgramSync(): [
  ProgramSyncState,
  {
    program: React.Dispatch<string>;
  }
] {
  const [initialDoc] = useState(loadInitialDocument);
  const [program, setProgram] = useBidirectionalState<string>(() =>
    initialDoc.program.toString()
  );
  const sq = useRef(newSerialQueue<any>());
  const amDoc = useRef<Automerge.FreezeObject<SolvyDocument>>(initialDoc);

  useEffect(() => {
    analyzeDiff(program, sq.current, amDoc);
  }, [program]);

  useEffect(() => {
    debugLog("localstorage_program_save");
    const bd = Automerge.save(amDoc.current);
    localStorage.setItem("solvy.document", toHexString(bd));
  }, [amDoc.current]);

  useEffect(() => {
    (globalThis as any).getAmDoc = () => amDoc.current;
    (globalThis as any).Automerge = Automerge;
  }, []);

  return [
    {
      program: program.current,
      // restoring from localStorage is synchronous, so there is never a
      // window where the editor has to wait on the document
      busy: false,
    },
    {
      program: setProgram.tx,
    },
  ];
}
